import { CheckCircle as CheckCircleIcon, ExpandMore as ExpandMoreIcon } from '@mui/icons-material'
import {
  Alert,
  Box,
  Checkbox,
  Chip,
  FormControlLabel,
  IconButton,
  Skeleton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import { useUploadedDocuments } from '@src/api/document-api'
import { EUploadedDocumentStatus, UploadedDocument } from '@src/types/automationDocument'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

interface UploadedDocumentsListProps {
  documentId: string
  filterByDocumentTypeIds?: number[]
  documentTypeNames?: Map<number, string>
  pageCountByTypeId?: Map<number, number>
  sentDocumentTypeIds?: number[]
  onExcludedTypeIdsChange?: (excludedTypeIds: number[]) => void
}

interface DuplicateGroup {
  typeId: number
  typeName: string
  pageCount: number
  newest: UploadedDocument
  older: UploadedDocument[]
}

function getStatusChipColor(statusId: number | null): 'success' | 'error' | 'warning' | 'info' | 'default' {
  switch (statusId) {
    case EUploadedDocumentStatus.Approuve:
      return 'success'
    case EUploadedDocumentStatus.Refuse:
      return 'error'
    case EUploadedDocumentStatus.EnAttente:
      return 'warning'
    case EUploadedDocumentStatus.EnApprobation:
      return 'info'
    default:
      return 'default'
  }
}

function getDocTypeName(doc: UploadedDocument, language: string) {
  return language === 'fr' ? doc.documentTypeNameFr : doc.documentTypeNameEn
}

function formatDate(dateStr: string | null) {
  if (!dateStr) return '-'
  return new Date(dateStr).toLocaleDateString()
}

const DocRow = ({ doc, t }: { doc: UploadedDocument; t: ReturnType<typeof useTranslation>['t'] }) => {
  return (
    <>
      <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {doc.fileName}
      </TableCell>
      <TableCell>
        <Chip
          label={t(`docsAutomation.uploadedDocuments.statusValues.${doc.statusId ?? 'unknown'}` as 'common.close')}
          color={getStatusChipColor(doc.statusId)}
          size="small"
        />
      </TableCell>
      <TableCell>{formatDate(doc.creationDT)}</TableCell>
      <TableCell>{doc.statusChangedByUserFullName || '-'}</TableCell>
    </>
  )
}

const DuplicateGroupRow = ({
  group,
  t,
  checked,
  isSent,
  onToggle,
}: {
  group: DuplicateGroup
  t: ReturnType<typeof useTranslation>['t']
  checked: boolean
  isSent: boolean
  onToggle: () => void
}) => {
  const [expanded, setExpanded] = useState(false)
  const hasOlder = group.older.length > 0

  return (
    <>
      <TableRow sx={isSent ? { opacity: 0.6 } : undefined}>
        <TableCell padding="checkbox">
          {isSent ? (
            <Tooltip title={t('docsAutomation.alreadySent' as 'common.close')}>
              <CheckCircleIcon color="success" fontSize="small" sx={{ ml: 1 }} />
            </Tooltip>
          ) : (
            <Checkbox size="small" checked={checked} onChange={onToggle} />
          )}
        </TableCell>
        <TableCell>
          {group.typeName}
          {hasOlder && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
              ({group.older.length + 1})
            </Typography>
          )}
        </TableCell>
        <TableCell align="center">
          <Chip label={group.pageCount} size="small" variant="outlined" />
        </TableCell>
        <DocRow doc={group.newest} t={t} />
        <TableCell padding="none" sx={{ width: 40 }}>
          {hasOlder && (
            <IconButton size="small" onClick={() => setExpanded(!expanded)}>
              <ExpandMoreIcon
                fontSize="small"
                sx={{
                  transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s',
                }}
              />
            </IconButton>
          )}
        </TableCell>
      </TableRow>
      {hasOlder &&
        expanded &&
        group.older.map((doc, i) => (
          <TableRow key={doc.documentId ?? i} sx={{ bgcolor: 'action.hover' }}>
            <TableCell padding="checkbox" />
            <TableCell />
            <TableCell />
            <DocRow doc={doc} t={t} />
            <TableCell padding="none" sx={{ width: 40 }} />
          </TableRow>
        ))}
    </>
  )
}

const DetailTable = ({
  documents,
  language,
  t,
}: {
  documents: UploadedDocument[]
  language: string
  t: ReturnType<typeof useTranslation>['t']
}) => {
  if (documents.length === 0) return null

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t('docsAutomation.uploadedDocuments.documentType')}</TableCell>
            <TableCell>{t('docsAutomation.uploadedDocuments.fileName')}</TableCell>
            <TableCell>{t('docsAutomation.uploadedDocuments.status')}</TableCell>
            <TableCell>{t('docsAutomation.uploadedDocuments.createdOn')}</TableCell>
            <TableCell>{t('docsAutomation.uploadedDocuments.statusChangedBy')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {documents.map((doc, index) => (
            <TableRow key={doc.documentId ?? index}>
              <TableCell>{getDocTypeName(doc, language) || '-'}</TableCell>
              <DocRow doc={doc} t={t} />
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

const UploadedDocumentsList = ({
  documentId,
  filterByDocumentTypeIds,
  documentTypeNames,
  pageCountByTypeId,
  sentDocumentTypeIds,
  onExcludedTypeIdsChange,
}: UploadedDocumentsListProps) => {
  const { t, i18n } = useTranslation()
  const [documents, isLoading, error] = useUploadedDocuments(documentId)
  const [showAll, setShowAll] = useState(false)
  const [userDeselectedTypeIds, setUserDeselectedTypeIds] = useState<Set<number>>(new Set())

  const { duplicateGroups, newTypeIds, others } = useMemo(() => {
    if (!filterByDocumentTypeIds || filterByDocumentTypeIds.length === 0) {
      return { duplicateGroups: [] as DuplicateGroup[], newTypeIds: [] as number[], others: [] as UploadedDocument[] }
    }
    const typeIdSet = new Set(filterByDocumentTypeIds)
    const groupMap = new Map<number, UploadedDocument[]>()
    const rest: UploadedDocument[] = []
    const matchedTypeIds = new Set<number>()

    for (const doc of documents) {
      const docTypeId = doc.documentTypeId != null ? Number(doc.documentTypeId) : null
      if (docTypeId != null && typeIdSet.has(docTypeId)) {
        matchedTypeIds.add(docTypeId)
        const list = groupMap.get(docTypeId) ?? []
        list.push(doc)
        groupMap.set(docTypeId, list)
      } else {
        rest.push(doc)
      }
    }

    const groups: DuplicateGroup[] = []
    for (const [typeId, docs] of groupMap) {
      // Sort by creationDT descending — newest first
      const sorted = [...docs].sort((a, b) => {
        const da = a.creationDT ? new Date(a.creationDT).getTime() : 0
        const db = b.creationDT ? new Date(b.creationDT).getTime() : 0
        return db - da
      })
      groups.push({
        typeId,
        typeName: getDocTypeName(sorted[0], i18n.language) || '-',
        pageCount: pageCountByTypeId?.get(typeId) ?? 0,
        newest: sorted[0],
        older: sorted.slice(1),
      })
    }

    // Types from pages that have no existing uploaded document
    const newTypes = filterByDocumentTypeIds.filter((id) => !matchedTypeIds.has(id))

    return { duplicateGroups: groups, newTypeIds: newTypes, others: rest }
  }, [documents, filterByDocumentTypeIds, i18n.language, pageCountByTypeId])

  // All duplicate types are selected by default; only track user deselections
  const selectedTypeIds = useMemo(() => {
    return new Set(duplicateGroups.map((g) => g.typeId).filter((id) => !userDeselectedTypeIds.has(id)))
  }, [duplicateGroups, userDeselectedTypeIds])

  const handleToggleType = (typeId: number) => {
    setUserDeselectedTypeIds((prev) => {
      const next = new Set(prev)
      if (next.has(typeId)) {
        next.delete(typeId)
      } else {
        next.add(typeId)
      }
      return next
    })
  }

  const handleToggleAll = () => {
    const allSelected = duplicateGroups.every((g) => selectedTypeIds.has(g.typeId))
    if (allSelected) {
      setUserDeselectedTypeIds(new Set(duplicateGroups.map((g) => g.typeId)))
    } else {
      setUserDeselectedTypeIds(new Set())
    }
  }

  const onExcludedTypeIdsChangeRef = useRef(onExcludedTypeIdsChange)
  onExcludedTypeIdsChangeRef.current = onExcludedTypeIdsChange

  const excludedTypeIds = useMemo(() => {
    return duplicateGroups.filter((g) => !selectedTypeIds.has(g.typeId)).map((g) => g.typeId)
  }, [duplicateGroups, selectedTypeIds])

  const excludedKey = excludedTypeIds.join(',')

  useEffect(() => {
    onExcludedTypeIdsChangeRef.current?.(excludedTypeIds)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [excludedKey])

  if (isLoading) {
    return (
      <Box sx={{ p: 1 }}>
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} variant="rectangular" height={32} sx={{ mb: 1 }} />
        ))}
      </Box>
    )
  }

  if (error) {
    const is503 = (error as { response?: { status?: number } })?.response?.status === 503
    return (
      <Alert severity="warning" sx={{ m: 1 }}>
        {is503
          ? t('docsAutomation.uploadedDocuments.serviceUnavailable')
          : t('docsAutomation.uploadedDocuments.loadError')}
      </Alert>
    )
  }

  if (documents.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
        {t('docsAutomation.uploadedDocuments.empty')}
      </Typography>
    )
  }

  const allSelected = duplicateGroups.length > 0 && duplicateGroups.every((g) => selectedTypeIds.has(g.typeId))
  const someSelected = duplicateGroups.some((g) => selectedTypeIds.has(g.typeId)) && !allSelected

  return (
    <Box>
      {duplicateGroups.length > 0 ? (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="warning.main" sx={{ mb: 1 }}>
            {t('docsAutomation.uploadedDocuments.duplicates', { count: duplicateGroups.length })}
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={allSelected}
                      indeterminate={someSelected}
                      onChange={handleToggleAll}
                    />
                  </TableCell>
                  <TableCell>{t('docsAutomation.uploadedDocuments.documentType')}</TableCell>
                  <TableCell align="center">{t('common.pages')}</TableCell>
                  <TableCell>{t('docsAutomation.uploadedDocuments.fileName')}</TableCell>
                  <TableCell>{t('docsAutomation.uploadedDocuments.status')}</TableCell>
                  <TableCell>{t('docsAutomation.uploadedDocuments.createdOn')}</TableCell>
                  <TableCell>{t('docsAutomation.uploadedDocuments.statusChangedBy')}</TableCell>
                  <TableCell padding="none" />
                </TableRow>
              </TableHead>
              <TableBody>
                {duplicateGroups.map((group) => (
                  <DuplicateGroupRow
                    key={group.typeId}
                    group={group}
                    t={t}
                    checked={selectedTypeIds.has(group.typeId)}
                    isSent={sentDocumentTypeIds?.includes(group.typeId) ?? false}
                    onToggle={() => handleToggleType(group.typeId)}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
          {t('docsAutomation.uploadedDocuments.noDuplicates')}
        </Typography>
      )}

      {newTypeIds.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="success.main" sx={{ mb: 1 }}>
            {t('docsAutomation.uploadedDocuments.newTypes', { count: newTypeIds.length })}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {newTypeIds.map((typeId) => {
              const count = pageCountByTypeId?.get(typeId) ?? 0
              const name = documentTypeNames?.get(typeId) ?? typeId
              const isAlreadySent = sentDocumentTypeIds?.includes(typeId) ?? false
              return (
                <Chip
                  key={typeId}
                  icon={isAlreadySent ? <CheckCircleIcon fontSize="small" /> : undefined}
                  label={isAlreadySent ? `${name} — ${t('docsAutomation.alreadySent' as 'common.close')}` : count > 0 ? `${name} (${count} p.)` : name}
                  size="small"
                  variant="outlined"
                  color={isAlreadySent ? 'default' : 'success'}
                  disabled={isAlreadySent}
                />
              )
            })}
          </Box>
        </Box>
      )}

      {others.length > 0 && (
        <>
          <FormControlLabel
            control={<Switch size="small" checked={showAll} onChange={(_, checked) => setShowAll(checked)} />}
            label={`${t('docsAutomation.uploadedDocuments.showAll')} (${others.length})`}
            sx={{ ml: 0 }}
          />
          {showAll && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                {t('docsAutomation.uploadedDocuments.otherDocuments', { count: others.length })}
              </Typography>
              <DetailTable documents={others} language={i18n.language} t={t} />
            </Box>
          )}
        </>
      )}
    </Box>
  )
}

export default UploadedDocumentsList
